import type { GraphDocument } from '@/contracts/funnel';

/**
 * SPEC-007 §20/§32/T038/T039 — the canonical recovery record and a small
 * interface so IndexedDB (or an in-memory fallback for tests/unsupported
 * browsers) is a swappable adapter, never a hardcoded dependency.
 */
export interface RecoveryRecord {
  userId: string;
  funnelId: string;
  baseRevision: number;
  schemaVersion: number;
  graph: GraphDocument;
  savedLocallyAt: string;
}

export interface RecoveryStore {
  get(userId: string, funnelId: string): Promise<RecoveryRecord | null>;
  put(record: RecoveryRecord): Promise<void>;
  remove(userId: string, funnelId: string): Promise<void>;
}

/**
 * FM-006 fallback: used when IndexedDB is unavailable/broken so the app
 * never crashes, and the caller (T044) knows to stop claiming local
 * protection is durable — this store's contents vanish with the tab.
 */
export class MemoryRecoveryStore implements RecoveryStore {
  private records = new Map<string, RecoveryRecord>();

  private key(userId: string, funnelId: string): string {
    return `${userId}:${funnelId}`;
  }

  async get(userId: string, funnelId: string): Promise<RecoveryRecord | null> {
    return this.records.get(this.key(userId, funnelId)) ?? null;
  }

  async put(record: RecoveryRecord): Promise<void> {
    this.records.set(this.key(record.userId, record.funnelId), record);
  }

  async remove(userId: string, funnelId: string): Promise<void> {
    this.records.delete(this.key(userId, funnelId));
  }
}
